<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import i18n from '@/i18n';
import { useRouter } from 'vue-router';
import { LANG_ENGLISH, LANG_FRENCH } from '@/constants';
import { getEnvVar } from '@/utils/env';
import { useLayout } from '@/layout/composables/layout';
import { useCalendar } from '@/i18n/calendar';
import { setLocalStorage } from '@/service/core';
import { UserInfo } from '@/modules/user/UserInfo';
import { useUserInfo } from '@/modules/user/useUserInfo';
import Button from 'primevue/button';

withDefaults(
  defineProps<{
    isLogged?: boolean;
  }>(),
  { isLogged: true }
);

const environment = getEnvVar('VITE_SITE');
const currentUser = ref<UserInfo>();
const outsideClickListener = ref(null);
const topbarMenuActive = ref(false);
const router = useRouter();
const userInfo = useUserInfo();
const { onMenuToggle } = useLayout();
const { changeLabelCalendarToFrench } = useCalendar();

const bindOutsideClickListener = () => {
  if (!outsideClickListener.value) {
    outsideClickListener.value = (event) => {
      if (isOutsideClicked(event)) {
        topbarMenuActive.value = false;
      }
    };
    document.addEventListener('click', outsideClickListener.value);
  }
};

const unbindOutsideClickListener = () => {
  if (outsideClickListener.value) {
    document.removeEventListener('click', outsideClickListener);
    outsideClickListener.value = null;
  }
};

const isOutsideClicked = (event) => {
  if (!topbarMenuActive.value) {
    return;
  }

  const sidebarEl = document.querySelector('.layout-topbar-menu');
  const topbarEl = document.querySelector('.layout-topbar-menu-button');

  return !(sidebarEl.isSameNode(event.target) || sidebarEl.contains(event.target) || topbarEl.isSameNode(event.target) || topbarEl.contains(event.target));
};

const signOut = () => {
  window.localStorage.clear();
  userInfo.resetUserInfo();
  setLocalStorage('language', i18n.global.locale.value);
  handleChangeRouter('landingPage');
};

const handleChange = (language: string) => {
  setLocalStorage('language', language);
  router.go(0);
};

const handleChangeRouter = (routerParam: string) => {
  router.push({ name: routerParam });
};

onMounted(async () => {
  currentUser.value = await userInfo.getUserInfo();
  bindOutsideClickListener();
  if (i18n.global.locale.value === LANG_FRENCH) {
    changeLabelCalendarToFrench();
  }
});

onBeforeUnmount(() => {
  unbindOutsideClickListener();
});
</script>
<template>
  <div :class="{ 'layout-topbar justify-content-between': isLogged, 'layout-topbar justify-content-end': !isLogged }">
    <Button v-if="isLogged" class="menu-button layout-menu-button layout-topbar-button mr-3" @click="onMenuToggle()">
      <i class="pi pi-bars"></i>
    </Button>

    <div class="flex align-content-center align-items-center">
      <div v-if="isLogged" class="mr-3">
        <div class="flex gap-2 min-h-20 align-items-center mb-1">
          <i class="pi pi-user text-primary"></i>
          <span>{{ currentUser?.name }}</span>
        </div>
        <div class="flex gap-2 min-h-20 align-items-center">
          <i class="pi pi-envelope text-primary"></i>
          <span>{{ currentUser?.email }}</span>
        </div>
      </div>
      <div v-if="isLogged" class="">
        <div class="flex gap-2 min-h-20 align-items-center mb-1">
          <i class="pi pi-building text-primary"></i>
          <span>{{ currentUser?.agency }}</span>
        </div>
        <div class="flex gap-2 min-h-20 align-items-center">
          <i class="pi pi-phone text-primary"></i>
          <span>{{ currentUser?.agencyPhone }}</span>
        </div>
      </div>
      <div v-if="environment === 'DEV'" class="ml-5 mr-3 white-space-nowrap">
        <span :class="i18n.global.locale.value === LANG_ENGLISH ? 'active' : 'hover-text'" @click="handleChange(LANG_ENGLISH)">EN</span> /
        <span :class="i18n.global.locale.value === LANG_FRENCH ? 'active' : 'hover-text'" @click="handleChange(LANG_FRENCH)">FR</span>
      </div>
      <div v-if="environment === 'DEV'">
        <div v-if="isLogged" class="flex cursor-pointer align-items-center" @click="signOut()">
          <i class="pi pi-sign-out"></i>
          <p class="ml-2 sm:inline">{{ $t('common.sign_out') }}</p>
        </div>
        <div v-else-if="!isLogged" class="flex cursor-pointer align-items-center" @click="handleChangeRouter('empty')">
          <i class="pi pi-sign-in"></i>
          <p class="ml-2 sm:inline">{{ $t('common.sign_in') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.layout-topbar {
  background-color: #f5f5f5;
  box-shadow: none;
  z-index: 1002;
}
.p-0 :deep(.p-inplace-display) {
  padding: 0 !important;
}
.layout-topbar .layout-topbar-button {
  min-width: 1.5rem;
  color: #707070;
}
.pButtonPrimary {
  background-color: var(bg-primary);
}
.p-overlay-badge {
  cursor: pointer;
}

.hover-text:hover {
  text-decoration: underline;
  cursor: pointer;
}

.active {
  text-decoration: underline;
  pointer-events: none;
}

.menu-button {
  text-align: left;
  background-color: transparent;
  margin: 0;
  padding: 0;
  border: 0;
  cursor: pointer;
  -webkit-user-select: none;
  user-select: none;
}
</style>
